/**
 * ITER297 · Part 4 — Manager-shell Ops Command Centre.
 *
 * Same KPI + Outlet Health + Department Health + Recurring Failures
 * primitives as the admin surface, but every backend endpoint auto-scopes
 * to the caller's assigned_outlet_ids (enforced server-side in
 * routes/ops_intelligence.py and routes/ops_compliance.py). We simply
 * point at the same endpoints — no "All Outlets" toggle, no portfolio
 * aggregation.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  RefreshCw, AlertTriangle, TrendingUp, Clock, Ticket as TicketIcon,
  Building2, ClipboardCheck, ArrowRight, Activity,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SectionBand, SectionTitle, KpiCard as SharedKpi } from '../components/ops/SectionBand';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const HEALTH_STYLE = {
  healthy:         { bg: '#DCFCE7', fg: '#166534', label: 'Healthy' },
  needs_attention: { bg: '#FEF3C7', fg: '#92400E', label: 'Needs Attention' },
  critical:        { bg: '#FEE2E2', fg: '#991B1B', label: 'Critical' },
  no_data:         { bg: '#F1F5F9', fg: '#64748B', label: 'No Data' },
};

function KpiCard({ label, value, tone, onClick, testId, suffix }) {
  const displayValue = `${value ?? '—'}${suffix || ''}`;
  const rag =
    tone === '#991B1B' || tone === '#B91C1C' ? 'poor' :
    tone === '#166534' ? 'good' :
    tone === '#92400E' ? 'warning' : null;
  return (
    <button onClick={onClick} data-testid={testId} className="text-left w-full">
      <SharedKpi label={label} value={displayValue} rag={rag} />
    </button>
  );
}

function HealthPill({ status }) {
  const s = HEALTH_STYLE[status] || HEALTH_STYLE.no_data;
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
          style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

export default function ManagerOpsCommandCentre() {
  const nav = useNavigate();
  const { user, getHeaders } = useAuth();

  const [kpis, setKpis] = useState(null);
  const [attention, setAttention] = useState(null);
  const [outletHealth, setOutletHealth] = useState([]);
  const [deptHealth, setDeptHealth] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [sectionsData, setSectionsData] = useState({ outlets: [] });
  const [expandedDept, setExpandedDept] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, a, o, d, r, s] = await Promise.all([
        axios.get(`${API}/api/ops/kpis`,             { headers: getHeaders() }),
        axios.get(`${API}/api/ops/needs-attention?limit=8`, { headers: getHeaders() }),
        axios.get(`${API}/api/ops/outlet-health`,    { headers: getHeaders() }),
        axios.get(`${API}/api/ops/department-health`,{ headers: getHeaders() }),
        axios.get(`${API}/api/ops/top-recurring-failures?limit=5&days=30`,
                                                       { headers: getHeaders() }),
        axios.get(`${API}/api/ops/department-sections`, { headers: getHeaders() }),
      ]);
      setKpis(k.data);
      setAttention(a.data);
      setOutletHealth(o.data?.outlets || []);
      setDeptHealth(d.data?.departments || []);
      setRecurring(r.data?.rows || []);
      setSectionsData(s.data || { outlets: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load dashboard');
    } finally { setLoading(false); }
  }, [getHeaders]);
  useEffect(() => { load(); }, [load]);

  // ITER300 · Context-aware navigation state.
  const NAV_STATE = { state: { from: '/manager/ops-command', fromLabel: 'Command Centre' } };

  const outletCount = outletHealth.length;
  const outletLabel = outletCount === 1 ? outletHealth[0]?.outlet_name : `${outletCount} outlets`;

  const openTicketId = (tid) => {
    if (tid) nav(`/manager/tickets?q=${encodeURIComponent(tid)}`, NAV_STATE);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="manager-ops-command-centre">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <SectionBand
          title="My Command Centre"
          subtitle={`Scope: ${outletLabel || '—'} · role: ${user?.role || ''}`}
          icon={Activity}
          testId="mgr-ops-page-band"
          actions={
            <Button variant="ghost" size="sm" onClick={load}
                     className="text-white/90 hover:text-white hover:bg-white/10"
                     data-testid="mgr-ops-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          }
        />

        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiCard label="Today Compliance" value={kpis.today_compliance_pct} suffix="%"
                     tone={kpis.today_compliance_pct >= 85 ? '#166534'
                            : kpis.today_compliance_pct >= 70 ? '#92400E' : '#991B1B'}
                     onClick={() => nav('/manager/compliance-intelligence', NAV_STATE)}
                     testId="mgr-kpi-today" />
            <KpiCard label="MTD Compliance" value={kpis.mtd_compliance_pct} suffix="%"
                     onClick={() => nav('/manager/compliance-intelligence', NAV_STATE)}
                     testId="mgr-kpi-mtd" />
            <KpiCard label="Pending Submissions" value={kpis.pending_submissions}
                     testId="mgr-kpi-pending" />
            <KpiCard label="Open Tickets" value={kpis.open_tickets}
                     onClick={() => nav('/manager/tickets?status=open,in_progress', NAV_STATE)}
                     testId="mgr-kpi-open" />
            <KpiCard label="Critical Pending" value={kpis.critical_pending}
                     tone={kpis.critical_pending ? '#991B1B' : undefined}
                     onClick={() => nav('/manager/tickets?status=open,in_progress&priority=critical', NAV_STATE)}
                     testId="mgr-kpi-critical" />
            <KpiCard label="Overdue" value={kpis.overdue_tickets}
                     tone={kpis.overdue_tickets ? '#B91C1C' : undefined}
                     onClick={() => nav('/manager/tickets?status=open,in_progress&overdue=true', NAV_STATE)}
                     testId="mgr-kpi-overdue" />
            <KpiCard label="Failed Points" value={kpis.failed_checklist_points}
                     testId="mgr-kpi-failed" />
          </div>
        )}

        <SectionTitle
          title="Needs Immediate Attention"
          icon={AlertTriangle}
          testId="mgr-band-needs-attention"
          actions={
            <span>{attention?.top_actionable?.length || 0} tickets</span>
          }
        />
        <div className="bg-white rounded-lg border border-[#E2E8DF] p-5 mb-6"
             data-testid="mgr-needs-attention-widget">
          {(!attention || attention.top_actionable.length === 0) ? (
            <div className="text-center py-6 text-slate-500 text-sm">
              Nothing critical right now.
            </div>
          ) : (
            <div className="space-y-1.5">
              {attention.top_actionable.slice(0, 6).map((t) => (
                <div key={t.id}
                     onClick={() => nav(`/manager/tickets?q=${t.ticket_id}`, NAV_STATE)}
                     className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[#F9F9F8] cursor-pointer border-l-4"
                     style={{ borderColor: t.overdue ? '#B91C1C' : t.priority === 'critical' ? '#991B1B' : '#F59E0B' }}>
                  <div className="font-mono text-[10px] text-slate-500 w-24 flex-shrink-0">
                    {t.ticket_id}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {t.outlet_name} · {t.department}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <SectionTitle title="Outlet Health" icon={Building2}
                     testId="mgr-band-outlet-health" />
        <div className="bg-white rounded-lg border border-[#E2E8DF] p-5 mb-6"
             data-testid="mgr-outlet-health-widget">
          <div className={`grid gap-3 ${outletCount === 1 ? 'grid-cols-1'
                          : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
            {outletHealth.map((o) => (
              <div key={o.outlet_id}
                   onClick={() => nav(`/manager/tickets?outlet_id=${o.outlet_id}&status=open,in_progress`, NAV_STATE)}
                   className="cursor-pointer border rounded-lg px-4 py-3 hover:shadow-sm"
                   data-testid={`mgr-outlet-card-${o.outlet_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{o.outlet_name}</div>
                    {o.city && <div className="text-[10px] text-slate-500">{o.city}</div>}
                  </div>
                  <HealthPill status={o.health} />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                  <div><span className="text-slate-500">Compliance </span>
                    <span className="font-semibold text-slate-800">{o.compliance_pct}%</span>
                  </div>
                  <div><span className="text-slate-500">Open </span>
                    <span className="font-semibold text-slate-800">{o.open_tickets}</span>
                  </div>
                  <div><span className="text-slate-500">Overdue </span>
                    <span className="font-semibold" style={{ color: o.overdue_tickets > 0 ? '#B91C1C' : '#334155' }}>
                      {o.overdue_tickets}
                    </span>
                  </div>
                  <div><span className="text-slate-500">Failed </span>
                    <span className="font-semibold text-slate-800">{o.failed_points}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <SectionTitle title="Department Health" icon={Activity}
                     subtitle="Expand any row for today's section-by-section breakdown"
                     testId="mgr-band-department-health" />
        <div className="bg-white rounded-lg border border-[#E2E8DF] p-5"
             data-testid="mgr-department-health-widget">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-primary)] text-white">
              <tr className="text-left text-[10px] uppercase tracking-[0.14em]">
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2">Department</th>
                <th className="px-3 py-2">Compliance</th>
                <th className="px-3 py-2">Open</th>
                <th className="px-3 py-2">Overdue</th>
                <th className="px-3 py-2">Failed</th>
                <th className="px-3 py-2">Health</th>
              </tr>
            </thead>
            <tbody>
              {deptHealth.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  No data.
                </td></tr>
              ) : deptHealth.map((d) => {
                const expanded = expandedDept === d.department;
                const outletsForDept = (sectionsData.outlets || [])
                  .flatMap((o) => (o.departments || [])
                    .filter((dp) => dp.department === d.department)
                    .map((dp) => ({ ...dp, outlet_name: o.outlet_name })));
                return (
                  <React.Fragment key={d.department}>
                    <tr className="border-b last:border-b-0 cursor-pointer hover:bg-[#F9F9F8]"
                        onClick={() => setExpandedDept((p) => p === d.department ? null : d.department)}
                        data-testid={`mgr-dept-row-${d.department}`}>
                      <td className="px-3 py-2.5 text-[#5C6B64]">{expanded ? '▾' : '▸'}</td>
                      <td className="px-3 py-2.5 text-sm font-medium">{d.department}</td>
                      <td className="px-3 py-2.5 text-xs">{d.compliance_pct}%</td>
                      <td className="px-3 py-2.5 text-xs">{d.open_tickets}</td>
                      <td className="px-3 py-2.5 text-xs font-semibold" style={{
                        color: d.overdue_tickets > 0 ? '#B91C1C' : '#334155',
                      }}>{d.overdue_tickets}</td>
                      <td className="px-3 py-2.5 text-xs">{d.failed_points}</td>
                      <td className="px-3 py-2.5"><HealthPill status={d.health} /></td>
                    </tr>
                    {expanded && (
                      <tr data-testid={`mgr-dept-sections-${d.department}`}>
                        <td colSpan={7} className="px-3 py-3 bg-[#F9F9F8] border-b">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {outletsForDept.flatMap((odp) => odp.sections.map((s) => (
                              <div key={`${odp.outlet_name}-${s.task_id}`}
                                   className="flex items-center gap-2 text-xs bg-white rounded px-2 py-1.5 border border-[#E2E8DF]">
                                <span className="font-mono w-4 text-center font-semibold"
                                      style={{ color: s.status === 'yes' ? '#059669'
                                                       : s.status === 'no' ? '#B91C1C' : '#92400E' }}>
                                  {s.status === 'yes' ? '✔' : s.status === 'no' ? '✖' : '◷'}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[#1A2421] truncate">{s.task_name}</div>
                                  <div className="text-[10px] text-[#5C6B64] truncate">{odp.outlet_name}</div>
                                </div>
                                {s.ticket_id && (
                                  <button onClick={(e) => { e.stopPropagation(); openTicketId(s.ticket_id); }}
                                          className="text-[10px] font-mono text-[color:var(--color-primary)] hover:underline">
                                    {s.ticket_id}
                                  </button>
                                )}
                              </div>
                            )))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
